/* eslint-disable @typescript-eslint/camelcase */
import {
  Checkbox,
  CircularProgress,
  Grid,
  makeStyles,
} from '@material-ui/core';
import i18n from 'i18n';
import { getAvailableApplicationsRequest } from 'modules/clients/actions';
import { cyrb53Hash } from 'utils/helpers';
import {
  ClientFields,
  ListAvailableApplicationsProps,
} from 'modules/clients/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import Button from 'components/Button';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import { useTableFilters, Query } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import headers from './headers';
import styles from './styles';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 10,
  search: '',
};

interface Props {
  client: ClientFields | null;
  applications: ListAvailableApplicationsProps;
  isLoading: boolean;
  onCancel: () => void;
  onSubmit: (values) => void;
  getAvailableApplicationsRequest: (props: {
    id: string;
    query: Query;
  }) => void;
}

const FormIncludeApplication = ({
  client,
  applications,
  isLoading,
  onCancel,
  onSubmit,
  getAvailableApplicationsRequest,
}: Props) => {
  const classes = useStyles();
  const {
    itemOrder,
    query,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const [items, setItems] = React.useState<
    Array<{ app_name: string; app_code: string; checked: boolean }>
  >([]);
  const [checks, setChecks] = React.useState<string[]>([]);
  const [selectedAll, setSelectedAll] = React.useState(false);

  const handleCheck = (app_code: string) => {
    const findCheckId = checks.find(item => item === app_code);
    if (!findCheckId) {
      setChecks([...checks, app_code]);
    } else {
      const index = checks.indexOf(findCheckId);
      checks.splice(index, 1);
      setChecks([...checks]);
    }
  };

  const handleAllCheck = () => {
    if (!selectedAll) {
      const ids: string[] = [];
      items.forEach(item => ids.push(item?.app_code));
      setChecks(ids);
    } else {
      setChecks([]);
    }
    setSelectedAll(!selectedAll);
  };

  const handleSubmit = () => onSubmit(checks);

  React.useEffect(() => {
    getAvailableApplicationsRequest({ id: client?.client_uuid || '', query });
  }, [client, getAvailableApplicationsRequest, query]);

  React.useEffect(
    () =>
      setItems(
        applications.results.map(item => ({
          ...item,
          checked: false,
        }))
      ),
    [applications]
  );

  return (
    <>
      <Grid container>
        <Grid container>
          <SearchBar
            id="search-app"
            isLoading={isLoading}
            placeholder={i18n.t('APPLICATIONS_PAGE.SEARCH_APPLICATION_KEY')}
            handleSearch={handleSearch}
            setSearchValue={setSearchValue}
          />
          <TableItems
            header={
              <>
                <TableCell
                  style={{ width: 40 }}
                  className={classes.checkboxCell}
                >
                  <Checkbox
                    onClick={handleAllCheck}
                    id="select-all-clients-users"
                  />
                </TableCell>
                {headers?.map(item => (
                  <TableCell
                    key={item?.key}
                    order={item?.order}
                    markOderItem={itemOrder === item?.key}
                    handleOrder={() =>
                      item?.order ? handleOrder(item?.key) : null
                    }
                    style={item?.style}
                    centerTitle={item?.centerTitle}
                  >
                    {item?.i18n}
                  </TableCell>
                ))}
              </>
            }
            overrideRowsCount={10}
            renderItems={item => (
              <TableRow key={cyrb53Hash(item?.app_name)}>
                <TableCell className={classes.checkboxCell}>
                  <Checkbox
                    onClick={() => handleCheck(item?.app_code)}
                    id="select-clients-users"
                    checked={!!checks.find(check => check === item?.app_code)}
                  />
                </TableCell>
                <TableCell>{item?.app_name}</TableCell>
                <TableCell centerTitle>{item?.app_code}</TableCell>
              </TableRow>
            )}
            data={applications}
            isLoading={isLoading}
            onChangePage={handlePageChange}
            onChangeLimit={handleLimitChange}
            notFoundText={i18n.t('APPLICATIONS_PAGE.NO_APPLICATIONS_FOUND_KEY')}
          />
        </Grid>
      </Grid>
      <Grid container justify="flex-end" className={classes.buttonsBase}>
        <Grid container className={classes.buttonsAlign}>
          <Button
            id="submit-button"
            onClick={handleSubmit}
            disabled={isLoading}
            submitButton
          >
            {isLoading ? <CircularProgress size={15} /> : i18n.t('ADD_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={onCancel}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

const mapStateToProps = ({
  clients: { clientSelected, listAvailableApplications },
}: ApplicationState) => ({
  client: clientSelected,
  isLoading: listAvailableApplications.loading,
  applications: listAvailableApplications,
});
export default connect(mapStateToProps, { getAvailableApplicationsRequest })(
  FormIncludeApplication
);
